import { useCommerce } from '../../commerce/ShopCommerce';
import { countWords, inStoreLanguage, shopWords, type ShopWords } from '../../commerce/shopWords';
import React, { useCallback, useRef, useState } from 'react';
import { useElementBox, useElementRender, type ElementComponent } from './elementRenderContext';
import { boolProp, choiceProp, numberProp, textProp, urlProp } from './elementProps';
import { SearchField } from './SearchField';
import { ELEMENT_FALLBACK_ICON, ELEMENT_ICONS } from './elementIcons';
import { useCloseOutside } from './useCloseOutside';
import './elementCommerce.css';
import { placeName } from './linkNames';

// The two parts of a header that belong to the shop. The bag draws what the cart holds — bound, so the
// number is the shop's answer and not something a merchant typed — and the search opens a field over
// the page rather than navigating away from it.

/** The glyph size both references settled on inside a 40px target. */
const GLYPH = 24;

// Spells the bag's accessible name with what is in it, in the store's language, since the badge itself is drawn for the eye only.
const cartLabel = (label: string, count: number, shown: boolean, words: ShopWords): string => (shown ? `${label}, ${countWords(count, words)}` : label);

// Draws the bag: a link to the cart, wearing the number of things waiting in it.
export const CartElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const Glyph = ELEMENT_ICONS[textProp(node.props, 'icon', 'shopping-bag')] ?? ELEMENT_FALLBACK_ICON;
  const commerce = useCommerce();
  const words = shopWords(commerce?.language);
  const count = commerce ? commerce.lines.reduce((sum, line) => sum + line.quantity, 0) : Math.max(0, Math.trunc(numberProp(node.props, 'count', 0)));
  const shown = boolProp(node.props, 'showCount', true) && count > 0;
  return (
    <a
      {...box}
      href={urlProp(node.props, 'href') ?? '/cart'}
      onClick={commerce ? (event) => { event.preventDefault(); commerce.open(); } : undefined}
      className={[box.className, 'xe-cart'].filter(Boolean).join(' ')}
      aria-label={cartLabel(inStoreLanguage(textProp(node.props, 'ariaLabel', ''), 'Cart', placeName('/cart', commerce?.language ?? 'en')), count, shown, words)}
    >
      <Glyph size={GLYPH} strokeWidth={1.5} aria-hidden="true" />
      {shown && <span className="xe-cart-count" aria-hidden="true">{count}</span>}
    </a>
  );
};

/** How the search is laid out, exactly as `searchElement` offers it. */
const SEARCH_LAYOUTS = ['glyph', 'field'] as const;

// Draws the search: a field on the page, the glyph opening the shop's search panel, or a field over the page where there is no shop.
export const SearchElement: ElementComponent = ({ node }) => {
  const commerce = useCommerce();
  const box = useElementBox(node);
  if (choiceProp(node.props, 'layout', SEARCH_LAYOUTS, 'glyph') === 'field') return <SearchField node={node} box={box} />;
  if (commerce) {
    const Glyph = ELEMENT_ICONS[textProp(node.props, 'icon', 'search')] ?? ELEMENT_FALLBACK_ICON;
    return (
      <div {...box} className={[box.className, 'xe-search'].filter(Boolean).join(' ')}>
        <button type="button" className="xe-search-toggle" aria-haspopup="dialog" aria-label={inStoreLanguage(textProp(node.props, 'ariaLabel', ''), 'Search', shopWords(commerce.language).search)} onClick={() => commerce.search(true)}>
          <Glyph size={GLYPH} strokeWidth={1.5} aria-hidden="true" />
        </button>
      </div>
    );
  }
  return <InlineSearch node={node} box={box} />;
};

// Draws the glyph and the field it opens over the page, for a page drawn outside the shop.
const InlineSearch: React.FC<{ node: Parameters<ElementComponent>[0]['node']; box: ReturnType<typeof useElementBox> }> = ({ node, box }) => {
  const [open, setOpen] = useState(false);
  const host = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useCloseOutside(host, open, close);
  const Glyph = ELEMENT_ICONS[textProp(node.props, 'icon', 'search')] ?? ELEMENT_FALLBACK_ICON;
  const label = inStoreLanguage(textProp(node.props, 'ariaLabel', ''), 'Search', shopWords(useElementRender().store?.language).search);
  const fieldId = `${node.domId ?? node.id}-q`;

  return (
    <div {...box} ref={host} className={[box.className, 'xe-search'].filter(Boolean).join(' ')}>
      <button
        type="button" className="xe-search-toggle" aria-expanded={open} aria-controls={fieldId}
        aria-label={label} onClick={() => setOpen((was) => !was)}
      >
        <Glyph size={GLYPH} strokeWidth={1.5} aria-hidden="true" />
      </button>
      <form
        id={fieldId} role="search" className="xe-search-form" data-open={open || undefined}
        action={urlProp(node.props, 'action') ?? '/search'} method="get"
      >
        <label className="xe-search-label" htmlFor={`${fieldId}-input`}>{label}</label>
        <input
          id={`${fieldId}-input`} className="xe-search-input" type="search" name="q" autoComplete="off"
          placeholder={textProp(node.props, 'placeholder', 'Search products')}
        />
      </form>
    </div>
  );
};
